/* Infinity cart limits v2. Product: 5 units; whole cart: 10 units.
 * Kalles cart events + serialized Ajax writes + a fresh checkout check.
 * No periodic cart polling or document-wide MutationObserver.
 */
(() => {
  'use strict';
  if (window.InfinityCart) return;
  const configElement = document.getElementById('infinity-cart-config');
  if (!configElement || !window.fetch) return;
  let config;
  try { config = { root: configElement.dataset.root, cart: JSON.parse(configElement.dataset.cart) }; } catch (_) { return; }
  const nativeFetch = window.fetch.bind(window);
  const root = (config.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const cartURL = new URL(`${root}cart.js`, location.origin).href;
  const isArabic = document.documentElement.lang.toLowerCase().startsWith('ar');
  const maxPerProduct = 5, maxTotal = 10;
  let campaignConfig = {};
  try { campaignConfig = JSON.parse(document.getElementById('infinity-bf-config')?.textContent || '{}'); } catch (_) {}
  const campaignNaturals = new Set((campaignConfig.naturalsIds || []).map(String));
  const campaignGiftIds = new Set((campaignConfig.gifts || []).map(gift => String(gift.variantId)));
  function productLimit(key) {
    const current = Date.now();
    return campaignConfig.enabled && current >= Date.parse(campaignConfig.start) && current < Date.parse(campaignConfig.end) && campaignNaturals.has(String(key).replace('product:', '')) ? 6 : maxPerProduct;
  }
  const checkoutSelector = '[name="checkout"], .checkout-button, .cart__checkout, .js_cart_tls_checkout, .hdt-mini-cart__btn-checkout, a[href*="/checkout"]';
  const addSelector = '.hdt-btn-add-to-cart, [name="add"], .add-to-cart, .shopify-payment-button__button, .hdt-btn-buy-now';
  const quantitySelector = 'input[will-change-cart], input[name="updates[]"][data-index]';
  const labels = isArabic ? {
    title: 'تنبيه بخصوص الطلب', subtitle: 'عذراً، لا يمكن إتمام الطلب بالشكل الحالي:', ok: 'حسناً', retry: 'تعذّر التحقق من السلة الآن. حاول مرة أخرى قبل إتمام الطلب.',
    item: title => `المنتج ${title}: الحد الأقصى ${maxPerProduct} قطع.`,
    total: `إجمالي المنتجات لا يمكن أن يتجاوز ${maxTotal} قطع.`,
    unlocked: '🎉 مبروك! حصلت على شحن مجاني',
    remaining: amount => `أضف منتجات بقيمة ${amount} إضافية للحصول على شحن مجاني`
  } : {
    title: 'Order Limit Alert', subtitle: 'Sorry, the order cannot be completed as is:', ok: 'OK', retry: 'We could not verify your cart. Please try again before checking out.',
    item: title => `Product ${title}: Maximum ${maxPerProduct} items.`,
    total: `Total items cannot exceed ${maxTotal} items.`,
    unlocked: '🎉 You’ve unlocked Free Shipping!',
    remaining: amount => `Add ${amount} more to get Free Shipping`
  };

  let cart = config.cart || { items: [], item_count: 0, total_price: 0 };
  let verified = false, pendingWrites = 0, revision = 0, checkoutBusy = false;
  let writeTail = Promise.resolve(), readPromise, refreshTimer, renderFrame, replay;
  let lastQuantityInteraction = -Infinity, popup;
  const previousAria = new WeakMap();
  const dirtyInputs = new Set();
  const productByVariant = new Map();
  const knownSnapshots = new Map();
  let snapshotSequence = 0;

  function signature(value) {
    return `${value.item_count}:${value.total_price}:` + (value.items || []).map(item => `${item.key || item.id}:${item.quantity}`).join('|');
  }
  function completeCart(value) {
    return value && Array.isArray(value.items) && Number.isFinite(value.item_count) && Number.isFinite(value.total_price);
  }
  function rememberProducts(value) {
    for (const item of value.items || []) {
      if (item.product_id) productByVariant.set(String(item.variant_id || item.id), { id: String(item.product_id), title: item.product_title || item.title });
    }
  }
  function productKey(item) {
    const variant = String(item.variant_id || item.id);
    return item.product_id ? `product:${item.product_id}` : productByVariant.has(variant) ? `product:${productByVariant.get(variant).id}` : `variant:${variant}`;
  }
  function totals(value = cart) {
    const products = new Map();
    let count = 0;
    for (const item of value.items || []) {
      if (campaignGiftIds.has(String(item.variant_id || item.id))) continue;
      const key = productKey(item);
      const entry = products.get(key) || { quantity: 0, title: item.product_title || item.title || 'Product' };
      entry.quantity += Number(item.quantity) || 0;
      count += Number(item.quantity) || 0;
      products.set(key, entry);
    }
    return { products, count };
  }
  function errorsForCart(value = cart, before) {
    const { products, count } = totals(value);
    const previous = before ? totals(before) : null;
    const errors = [];
    products.forEach((item, key) => {
      // A customer must always be able to reduce an already-invalid cart.
      const limit = productLimit(key);
      if (item.quantity > limit && (!previous || item.quantity > (previous.products.get(key)?.quantity || 0))) errors.push(isArabic ? `المنتج ${item.title}: الحد الأقصى ${limit} قطع.` : `Product ${item.title}: Maximum ${limit} items.`);
    });
    if (count > maxTotal && (!previous || count > previous.count)) errors.push(labels.total);
    return errors;
  }
  function showPopup(errors) {
    if (!popup) {
      popup = document.createElement('dialog');
      popup.id = 'order-limit-popup';
      popup.dir = isArabic ? 'rtl' : 'ltr';
      popup.setAttribute('aria-labelledby', 'infinity-order-limit-title');
      popup.innerHTML = '<h3 id="infinity-order-limit-title"></h3><p></p><ul></ul><button type="button"></button>';
      popup.querySelector('h3').textContent = labels.title;
      popup.querySelector('p').textContent = labels.subtitle;
      const button = popup.querySelector('button');
      button.textContent = labels.ok;
      button.addEventListener('click', () => popup.close());
      document.body.appendChild(popup);
    }
    const items = [...new Set(errors)].map(message => {
      const item = document.createElement('li');
      item.textContent = message;
      return item;
    });
    popup.querySelector('ul').replaceChildren(...items);
    if (!popup.open) {
      if (typeof popup.showModal === 'function') popup.showModal();
      else window.alert(errors.join('\n'));
    }
  }
  function setText(element, value) {
    if (element && element.textContent !== value) element.textContent = value;
  }

  function renderCheckout() {
    const invalid = errorsForCart().length > 0;
    const busy = checkoutBusy || pendingWrites > 0;
    document.querySelectorAll(checkoutSelector).forEach(button => {
      if (invalid || busy) {
        if (!previousAria.has(button)) previousAria.set(button, button.getAttribute('aria-disabled'));
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-infinity-cart-state', invalid ? 'limit' : 'checking');
        button.classList.add('inf-order-limit-blocked');
      } else if (previousAria.has(button) || button.hasAttribute('data-infinity-cart-state')) {
        const value = previousAria.get(button);
        if (value == null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', value);
        button.classList.remove('inf-order-limit-blocked');
        button.removeAttribute('data-infinity-cart-state');
        previousAria.delete(button);
      }
      // Do not set native disabled: clicks must still reach the validation gate,
      // and another component may own a required-terms/sold-out disabled state.
    });
  }
  function renderShipping() {
    renderFrame = 0;
    const container = document.getElementById('custom-shipping-progress-wrapper');
    if (!container) return;
    const display = Number(cart.item_count) > 0 ? 'block' : 'none';
    if (container.style.display !== display) container.style.display = display;
    if (display === 'none') return;
    const direction = isArabic ? 'rtl' : 'ltr';
    if (container.getAttribute('dir') !== direction) container.setAttribute('dir', direction);
    const threshold = Number(container.dataset.threshold) || 60000;
    const total = Number(cart.total_price) || 0;
    const unlocked = total >= threshold;
    const fill = document.getElementById('shipping-progress-fill');
    const message = document.getElementById('shipping-progress-message');
    if (fill) {
      const width = `${Math.max(0, Math.min(100, total / threshold * 100))}%`;
      if (fill.style.width !== width) fill.style.width = width;
      fill.classList.toggle('completed', unlocked);
    }
    if (message) {
      const amount = `${((threshold - total) / 100).toFixed(2)} ${cart.currency || 'EGP'}`;
      setText(message, unlocked ? labels.unlocked : labels.remaining(amount));
      message.classList.toggle('inf-shipping-unlocked', unlocked);
    }
  }

  function render() {
    renderCheckout();
    window.dispatchEvent(new CustomEvent('infinity:cart-updated', { detail: { cart } }));
    if (!renderFrame) renderFrame = requestAnimationFrame(renderShipping);
  }
  function acceptCart(next, show = false, remember = false) {
    if (!completeCart(next)) return false;
    const wasInvalid = errorsForCart().length > 0;
    const changed = signature(cart) !== signature(next);
    cart = next;
    verified = true;
    rememberProducts(cart);
    if (remember) {
      knownSnapshots.set(signature(cart), ++snapshotSequence);
      if (knownSnapshots.size > 20) knownSnapshots.delete(knownSnapshots.keys().next().value);
    }
    render();
    const errors = errorsForCart();
    if (wasInvalid && !errors.length && popup?.open) popup.close();
    for (const input of dirtyInputs) {
      if (!input.isConnected || Number(input.value) === Number(lineFor(input)?.quantity)) dirtyInputs.delete(input);
    }
    if (show && changed && errors.length) showPopup(errors);
    return true;
  }
  async function readCart() {
    if (readPromise) return readPromise;
    const started = revision;
    readPromise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await nativeFetch(cartURL, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart unavailable');
        const next = await response.json();
        if (!completeCart(next)) throw new Error('Invalid cart response');
        if (started !== revision) return false;
        acceptCart(next, false, true);
        return true;
      } finally { clearTimeout(timeout); }
    })();
    try { return await readPromise; } finally { readPromise = null; }
  }
  async function freshCart() {
    // One stale read can overlap a queued write; retries are bounded to this action.
    for (let attempt = 0; attempt < 3; attempt++) if (await readCart()) return true;
    throw new Error('Cart changed while checking');
  }
  function queueRefresh(show = false) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        await writeTail;
        await freshCart();
        if (show && errorsForCart().length) showPopup(errorsForCart());
      } catch (_) { verified = false; }
      render();
    }, 120);
  }
  function mutationKind(input, init) {
    try {
      const value = typeof input === 'string' ? input : input?.url || input?.href;
      if (!value) return null;
      const url = new URL(value, location.href);
      const method = (init?.method || input?.method || 'GET').toUpperCase();
      return url.origin === location.origin && method !== 'GET' && method !== 'HEAD' ? url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/)?.[1] : null;
    } catch (_) { return null; }
  }
  function formProduct(form) {
    const variant = form?.elements?.namedItem('id')?.value;
    const product = form?.elements?.namedItem('product-id')?.value || form?.closest('[data-product-id]')?.dataset.productId;
    const title = form?.closest('hdt-card-product')?.querySelector('.hdt-card-product__title')?.textContent.trim() || document.querySelector('h1')?.textContent.trim();
    if (variant && product) productByVariant.set(String(variant), { id: String(product), title });
  }
  function fromEntries(entries) {
    const data = {}, items = new Map(), updates = {};
    for (const [name, value] of entries) {
      let match;
      if ((match = name.match(/^items\[(\d+)\]\[(id|quantity)\]$/))) {
        const item = items.get(match[1]) || {};
        item[match[2]] = value; items.set(match[1], item);
      } else if ((match = name.match(/^updates\[([^\]]*)\]$/))) {
        if (match[1]) updates[match[1]] = value;
        else (data.updates ||= []).push(value);
      } else if (['id', 'line', 'quantity'].includes(name)) data[name] = value;
    }
    if (items.size) data.items = [...items.values()];
    if (Object.keys(updates).length) data.updates = updates;
    return data;
  }
  async function requestData(input, init) {
    let body = init?.body;
    if (body === undefined && typeof input?.clone === 'function') {
      const copy = input.clone();
      if ((copy.headers.get('content-type') || '').includes('multipart/form-data')) return fromEntries((await copy.formData()).entries());
      body = await copy.text();
    }
    if (!body) return {};
    if (typeof body.entries === 'function') return fromEntries(body.entries());
    if (typeof body !== 'string') return {};
    try { return JSON.parse(body); } catch (_) { return fromEntries(new URLSearchParams(body).entries()); }
  }
  function project(kind, data, base = cart) {
    const next = { ...base, items: base.items.map(item => ({ ...item })) };
    if (kind === 'clear') next.items = [];
    else if (kind === 'add') {
      for (const input of data.items || (data.id ? [data] : [])) {
        const id = String(input.id || '');
        const quantity = Number(input.quantity ?? 1);
        if (!id || !Number.isFinite(quantity) || quantity < 0) continue;
        const known = productByVariant.get(id);
        // Keeping additions separate also counts split properties/selling-plan lines.
        next.items.push({ id, variant_id: id, product_id: known?.id, product_title: known?.title, quantity });
      }
    } else if (kind === 'change') {
      const item = data.line ? next.items[Number(data.line) - 1] : next.items.find(item => item.key === String(data.id) || String(item.variant_id || item.id) === String(data.id));
      if (item && data.quantity != null && Number.isFinite(Number(data.quantity))) item.quantity = Math.max(0, Number(data.quantity));
    } else if (kind === 'update' && data.updates) {
      if (Array.isArray(data.updates)) data.updates.forEach((value, index) => { if (next.items[index]) next.items[index].quantity = Math.max(0, Number(value) || 0); });
      else for (const [id, value] of Object.entries(data.updates)) {
        const item = next.items.find(item => item.key === id || String(item.variant_id || item.id) === id);
        if (item) item.quantity = Math.max(0, Number(value) || 0);
      }
    }
    return next;
  }
  function errorResponse(errors) {
    const message = errors.join('\n');
    return new Response(JSON.stringify({ status: 422, message, description: message, errors: errors.map(message => ({ message })) }), { status: 422, headers: { 'Content-Type': 'application/json' } });
  }
  window.fetch = function (...args) {
    const kind = mutationKind(args[0], args[1]);
    if (!kind) return nativeFetch(...args);
    const queuedLineKeys = cart.items.map(item => item.key || String(item.id));
    const payload = requestData(args[0], args[1]);
    pendingWrites++;
    revision++;
    clearTimeout(refreshTimer);
    renderCheckout();
    const task = writeTail.catch(() => {}).then(async () => {
      try {
        let data = await payload;
        let requestArgs = args;
        // Section/discount/note refreshes are not quantity increases.
        const quantityWrite = kind === 'add' || kind === 'change' || kind === 'clear' || data.updates;
        if (quantityWrite) {
          if (!verified) await freshCart();
          if (kind === 'change' && data.line && queuedLineKeys[Number(data.line) - 1]) {
            const key = queuedLineKeys[Number(data.line) - 1];
            const index = cart.items.findIndex(item => (item.key || String(item.id)) === key);
            if (index < 0) return errorResponse([labels.retry]);
            if (index !== Number(data.line) - 1) {
              // Kalles sends JSON line numbers. Preserve the intended item if an
              // earlier queued remove/add changes the server's line ordering.
              if (typeof args[1]?.body !== 'string' || !args[1].body.trim().startsWith('{')) return errorResponse([labels.retry]);
              data = { ...data, line: index + 1 };
              requestArgs = [args[0], { ...args[1], body: JSON.stringify(data) }];
            }
          }
          const giftError = window.InfinityCampaign?.giftRequestError(kind, data, cart);
          if (giftError) { showPopup([giftError]); return errorResponse([giftError]); }
          const errors = errorsForCart(project(kind, data), cart);
          if (errors.length) {
            showPopup(errors);
            restoreQuantities();
            return errorResponse(errors);
          }
        }
        const response = await nativeFetch(...requestArgs);
        if (response.ok) {
          let result;
          try { result = await response.clone().json(); } catch (_) { /* Reconcile below. */ }
          if (!acceptCart(result, true, true)) {
            verified = false;
            try { await freshCart(); } catch (_) { /* Checkout remains fail-closed. */ }
          }
          if (errorsForCart().length) showPopup(errorsForCart());
        } else {
          verified = false;
        }
        return response;
      } catch (error) {
        verified = false;
        throw error;
      } finally {
        pendingWrites--;
        render();
      }
    });
    writeTail = task.then(() => {}, () => {});
    return task;
  };
  function cartInputs(scope = document) {
    return [...scope.querySelectorAll(quantitySelector)];
  }
  function lineFor(input) {
    return cart.items[Number(input?.dataset.index) - 1];
  }
  function quantityProposal(input, quantity) {
    const scope = input.closest('cart-items-component, hdt-main-cart, form') || document;
    const updates = {};
    for (const field of cartInputs(scope)) {
      const item = lineFor(field);
      if (item) updates[item.key || item.id] = field === input ? quantity : Number(field.value);
    }
    return project('update', { updates });
  }
  function restoreQuantities() {
    dirtyInputs.clear();
    for (const input of cartInputs()) {
      const item = lineFor(input);
      if (item) input.value = item.quantity;
    }
  }
  function stop(event, errors) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (errors?.length) showPopup(errors);
  }
  function addErrors(form) {
    if (!form) return [];
    formProduct(form);
    const data = fromEntries(new FormData(form).entries());
    return errorsForCart(project('add', data), cart);
  }
  async function waitForWrites() {
    for (let attempt = 0; attempt < 12; attempt++) {
      const tail = writeTail;
      await tail;
      if (tail === writeTail && !pendingWrites) return;
    }
    throw new Error('Cart is still changing');
  }
  function delay(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }
  async function settleQuantities() {
    // Kalles debounces +/- by 300ms. Wait for that pending change before checking.
    const remaining = 350 - (Date.now() - lastQuantityInteraction);
    if (remaining > 0) await delay(remaining);
    await waitForWrites();
    for (const input of [...dirtyInputs]) {
      const item = lineFor(input);
      if (!input.isConnected || !item || Number(input.value) === Number(item.quantity)) { dirtyInputs.delete(input); continue; }
      const errors = errorsForCart(quantityProposal(input, Number(input.value)), cart);
      if (errors.length) { showPopup(errors); restoreQuantities(); return false; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    await waitForWrites();
    return true;
  }
  async function gateCheckout(activate) {
    if (checkoutBusy) return;
    checkoutBusy = true;
    renderCheckout();
    let approved = false;
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        if (!await settleQuantities()) return;
        await waitForWrites();
        await window.InfinityCampaign?.syncGifts(true);
        await waitForWrites();
        const started = revision;
        await freshCart();
        if (started !== revision || pendingWrites) continue;
        const errors = [...errorsForCart(), ...(window.InfinityCampaign?.validateGifts(cart) || [])];
        if (errors.length) { showPopup(errors); return; }
        approved = true;
        break;
      }
      if (!approved) throw new Error('Cart is still changing');
    } catch (_) {
      showPopup([labels.retry]);
    } finally {
      checkoutBusy = false;
      renderCheckout();
    }
    if (approved) activate();
  }
  function currentControl(button, form) {
    if (button?.isConnected) return button;
    const activeForm = form?.id ? document.getElementById(form.id) : form;
    return activeForm ? [...document.querySelectorAll(checkoutSelector)].find(node => node.form === activeForm || activeForm.contains(node)) : null;
  }
  function activateCheckout(button, form) {
    const control = currentControl(button, form);
    const activeForm = control?.form || (form?.id ? document.getElementById(form.id) : form);
    replay = { control, form: activeForm, revision };
    try {
      if (control) control.click();
      else if (activeForm?.isConnected) activeForm.requestSubmit();
    } finally { replay = null; }
  }
  // Capture on window, before component and app click/submit listeners.
  window.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return;
    const quantityButton = target.closest('hdt-quantity-wrapp button[name="plus"], hdt-quantity-wrapp button[name="minus"]');
    if (quantityButton) {
      const input = quantityButton.closest('hdt-quantity-wrapp').querySelector(quantitySelector);
      if (input) {
        const quantity = Number(input.value) + (quantityButton.name === 'plus' ? 1 : -1) * (Number(input.step) || 1);
        const errors = errorsForCart(quantityProposal(input, Math.max(0, quantity)), cart);
        if (errors.length) return stop(event, errors);
        lastQuantityInteraction = Date.now();
        dirtyInputs.add(input);
        revision++;
      }
    }
    const checkout = target.closest(checkoutSelector);
    if (checkout) {
      if (replay?.control === checkout && replay.revision === revision) return;
      stop(event);
      const form = checkout.form || checkout.closest('form');
      gateCheckout(() => activateCheckout(checkout, form));
      return;
    }
    const add = target.closest(addSelector);
    if (add) {
      const errors = addErrors(add.form || add.closest('form'));
      if (errors.length) stop(event, errors);
    }
  }, true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const path = new URL(form.action, location.href).pathname;
    if (/\/cart\/add(?:\.js)?\/?$/.test(path)) {
      const errors = addErrors(form);
      if (errors.length) stop(event, errors);
    } else if (event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(path) || (/\/cart\/?$/.test(path) && !event.submitter)) {
      if (replay?.form === form && replay.revision === revision) return;
      stop(event);
      gateCheckout(() => activateCheckout(event.submitter, form));
    }
  }, true);
  window.addEventListener('input', event => {
    if (event.target instanceof Element && event.target.matches(quantitySelector)) {
      lastQuantityInteraction = Date.now();
      dirtyInputs.add(event.target);
      revision++;
    }
  }, true);
  window.addEventListener('quantity-selector:update', event => {
    const input = event.target instanceof Element ? event.target.closest(quantitySelector) : null;
    if (!input || !event.detail?.line) return;
    const errors = errorsForCart(quantityProposal(input, Number(event.detail.quantity)), cart);
    if (errors.length) { stop(event, errors); restoreQuantities(); }
  }, true);
  window.addEventListener('cart:update', event => {
    const data = event.detail?.cartData || event.detail?.cart || event.detail?.resource;
    if (completeCart(data)) {
      const known = knownSnapshots.get(signature(data));
      // Animated Kalles renders can deliver an earlier write after a later one.
      if ((known === undefined && !pendingWrites) || known === snapshotSequence) acceptCart(data, true, known === undefined);
      else queueRefresh(known === undefined);
    } else queueRefresh(true);
    const detail = event.detail;
    if (detail?.source === 'product-form-component' && (detail.actionAfterATC || window.themeHDN?.settings?.actionAfterATC) === 'go_checkout') {
      detail.actionAfterATC = 'no_action';
      gateCheckout(() => location.assign(`${root}checkout`));
    }
  }, true);
  ['cart:updated', 'ajaxCart:updated', 'shopify:cart:updated', 'cart:refresh', 'cart:reload'].forEach(name => {
    document.addEventListener(name, event => {
      const data = event.detail?.cart || event.detail?.cartData || event.detail;
      if (!pendingWrites && completeCart(data)) acceptCart(data, true, true);
      else queueRefresh(true);
    });
  });
  ['cart:drawer:render', 'cart:main:render', 'shopify:section:load'].forEach(name => document.addEventListener(name, render));
  document.addEventListener('dialog:opening', event => {
    if (event.target instanceof Element && (event.target.id === 'CartDrawer' || event.target.closest('hdt-cart-drawer'))) { render(); queueRefresh(); }
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted || performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') {
      verified = false; replay = null; checkoutBusy = false;
      render(); queueRefresh();
      if (!event.persisted) document.dispatchEvent(new CustomEvent('cart:reload'));
    }
  });
  rememberProducts(cart);
  window.InfinityCart = { version: '2.1.0-bf', refresh: queueRefresh, getCart: () => cart };
  render();
})();
